import {
  FUTURES_AMOUNT_PRESETS,
  FUTURES_AMOUNT_RATIOS,
} from '../constants/futuresPeriod';

export { FUTURES_AMOUNT_PRESETS, FUTURES_AMOUNT_RATIOS };

const DEFAULT_AMOUNT_PRESETS = [10, 50, 300, 500, 1000];
const DEFAULT_AMOUNT_RATIOS = [0.2, 0.25, 0.5, 0.75, 1];

/** 從 playConfig 解析快速下單預設 */
export function parseQuickOrderConfig(playConfig) {
  const empty = {
    amountPresets: DEFAULT_AMOUNT_PRESETS,
    amountRatios: DEFAULT_AMOUNT_RATIOS,
  };
  if (!playConfig) {
    return empty;
  }
  try {
    const json = typeof playConfig === 'string' ? JSON.parse(playConfig) : playConfig;
    const amountPresets = String(json.quickAmounts || '')
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((item) => item > 0)
      .slice(0, 5);
    const amountRatios = String(json.quickRatios || '')
      .split(',')
      .map((item) => Number(item.trim()) / 100)
      .filter((item) => item > 0 && item <= 1)
      .slice(0, 5);
    return {
      amountPresets: amountPresets.length ? amountPresets : DEFAULT_AMOUNT_PRESETS,
      amountRatios: amountRatios.length ? amountRatios : DEFAULT_AMOUNT_RATIOS,
    };
  } catch {
    return empty;
  }
}

/** 快速金額：不超過可用餘額與商品最大下單額 */
export function resolveQuickOrderAmount(rawAmount, balance, product) {
  let amount = Number(rawAmount);
  if (Number.isNaN(amount) || amount <= 0) {
    return null;
  }
  const balanceNum = Number(balance);
  if (!Number.isNaN(balanceNum) && balanceNum >= 0) {
    amount = Math.min(amount, balanceNum);
  }
  const max = Number(product?.maxOrderAmount);
  if (!Number.isNaN(max) && max > 0) {
    amount = Math.min(amount, max);
  }
  return amount > 0 ? amount : null;
}

export function formatOrderAmountInput(value) {
  const num = Number(value);
  if (Number.isNaN(num) || num <= 0) {
    return '';
  }
  return String(Number(num.toFixed(2)));
}

/** 0 表示不限制（與後台一致） */
export function isWithinAmountLimits(amount, product) {
  const bet = Number(amount);
  if (!bet || bet <= 0) {
    return false;
  }
  const min = Number(product?.minOrderAmount);
  const max = Number(product?.maxOrderAmount);
  if (!Number.isNaN(min) && min > 0 && bet < min) {
    return false;
  }
  if (!Number.isNaN(max) && max > 0 && bet > max) {
    return false;
  }
  return true;
}

export function validateOrderAmount(amount, product, balance, handlingFeeRate) {
  const bet = Number(amount);
  if (!bet || bet <= 0) {
    return { ok: false, message: '請輸入有效下單金額' };
  }

  const min = Number(product?.minOrderAmount);
  const max = Number(product?.maxOrderAmount);
  if (!Number.isNaN(min) && min > 0 && bet < min) {
    return { ok: false, message: `最小下單金額 ${min} USDT` };
  }
  if (!Number.isNaN(max) && max > 0 && bet > max) {
    return { ok: false, message: `最大下單金額 ${max} USDT` };
  }

  const rate = Number(handlingFeeRate) || 0;
  const fee = bet * rate;
  const totalCost = bet + fee;
  const balanceNum = Number(balance);
  if (balance != null && balance !== '—' && !Number.isNaN(balanceNum) && totalCost > balanceNum) {
    return { ok: false, message: 'USDT 餘額不足' };
  }

  return { ok: true, fee, margin: bet, totalCost };
}

/** 交割：保證金 = 下單金額 */
export function calcFuturesOrderPreview(amount, handlingFeeRate) {
  const bet = Number(amount) || 0;
  const rate = Number(handlingFeeRate) || 0;
  const fee = bet * rate;
  return {
    margin: bet,
    fee,
    totalCost: bet + fee,
  };
}

/** 限時：預期收益 = 本金 × 賠率 */
export function calcTimedOrderPreview(amount, handlingFeeRate, payoutOdds) {
  const bet = Number(amount) || 0;
  const rate = Number(handlingFeeRate) || 0;
  const odds = Number(payoutOdds) || 0;
  const fee = bet * rate;
  return {
    margin: bet,
    fee,
    totalCost: bet + fee,
    expectedProfit: bet * odds,
  };
}

/** 永續：保證金 = 名義金額 / 槓桿，手續費 = 名義金額 × 費率 */
export function calcPerpetualOrderPreview(amount, handlingFeeRate, leverage) {
  const bet = Number(amount) || 0;
  const lev = Number(leverage) || 1;
  const rate = Number(handlingFeeRate) || 0;
  const fee = bet * rate;
  const margin = lev > 0 ? bet / lev : bet;
  return {
    margin,
    fee,
    totalCost: margin + fee,
    betAmount: bet,
  };
}

export function validatePerpetualAmount(amount, product, balance, handlingFeeRate, leverage) {
  const bet = Number(amount);
  if (!bet || bet <= 0) {
    return { ok: false, message: '請輸入有效下單金額' };
  }

  const min = Number(product?.minOrderAmount);
  const max = Number(product?.maxOrderAmount);
  if (!Number.isNaN(min) && min > 0 && bet < min) {
    return { ok: false, message: `最小下單金額 ${min} USDT` };
  }
  if (!Number.isNaN(max) && max > 0 && bet > max) {
    return { ok: false, message: `最大下單金額 ${max} USDT` };
  }

  const preview = calcPerpetualOrderPreview(bet, handlingFeeRate, leverage);
  const balanceNum = Number(balance);
  if (balance != null && balance !== '—' && !Number.isNaN(balanceNum) && preview.totalCost > balanceNum) {
    return { ok: false, message: 'USDT 餘額不足' };
  }

  return { ok: true, ...preview };
}
